import { useEffect, useState } from "react";
import { createFileRoute, useNavigate, useRouter } from "@tanstack/react-router";

import { MIN_PASSWORD } from "@/braining/partners";
import { BrainingLogo } from "@/braining/components/braining-logo";
import {
  acceptStaffInviteFn,
  peekStaffInviteFn,
  type StaffPeek,
} from "@/server/staff-invites.functions";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

/**
 * Приглашение сотрудника (0035): админ отправил ссылку, человек задаёт себе
 * пароль и сразу входит. Токен — после `#`: на сервер браузер эту часть
 * адреса не шлёт, страница передаёт его POST-запросом.
 */
export const Route = createFileRoute("/join")({
  head: () => ({
    meta: [
      { title: "Приглашение — Портал Braining Group" },
      { name: "referrer", content: "no-referrer" },
    ],
  }),
  component: JoinPage,
});

const ROLE_LABEL: Record<string, string> = {
  admin: "администратор",
  manager: "менеджер смет",
  rop: "руководитель",
  docs: "документы с подрядчиками",
};

function JoinPage() {
  const [token, setToken] = useState("");
  const [peek, setPeek] = useState<StaffPeek | null>(null);
  const [password, setPassword] = useState("");
  const [repeat, setRepeat] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const navigate = useNavigate();
  const router = useRouter();

  useEffect(() => {
    const t = window.location.hash.replace(/^#/, "");
    setToken(t);
    peekStaffInviteFn({ data: { token: t } })
      .then(setPeek)
      .catch((e: unknown) => {
        console.error("[join] ссылку не проверили", e);
        setPeek({ ok: false, error: "Ссылка недействительна или устарела" });
      });
  }, []);

  async function onSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (busy) return;
    if (password !== repeat) {
      setError("Пароли не совпадают");
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const res = await acceptStaffInviteFn({ data: { token, password } });
      if (!res.ok) {
        setError(res.error);
        return;
      }
      await router.invalidate();
      await navigate({ to: "/" });
    } catch (err) {
      console.error("[join] не удалось принять приглашение", err);
      setError("Не получилось. Попробуйте ещё раз.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="flex min-h-screen items-center justify-center px-4">
      <div className="w-full max-w-sm">
        <div className="mb-8 flex justify-center">
          <BrainingLogo className="h-11 w-auto text-foreground" />
        </div>
        {!peek ? (
          <p className="text-center text-sm text-muted-foreground">Проверяем ссылку…</p>
        ) : !peek.ok ? (
          <div className="space-y-2 rounded-xl border bg-card p-6 text-sm shadow-sm">
            <h1 className="text-lg font-semibold">Ссылка не сработала</h1>
            <p className="text-muted-foreground">{peek.error}</p>
          </div>
        ) : (
          <form onSubmit={onSubmit} className="space-y-4 rounded-xl border bg-card p-6 shadow-sm">
            <div>
              <h1 className="text-lg font-semibold">Вход в портал Braining</h1>
              <p className="mt-1 text-sm text-muted-foreground">
                Роль: {ROLE_LABEL[peek.role] ?? peek.role}
              </p>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="email">Почта для входа</Label>
              <Input id="email" type="email" autoComplete="username" value={peek.email} readOnly />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="password">Придумайте пароль</Label>
              <Input
                id="password"
                type="password"
                autoComplete="new-password"
                minLength={MIN_PASSWORD}
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                disabled={busy}
              />
              <p className="text-xs text-muted-foreground">Не короче {MIN_PASSWORD} знаков.</p>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="repeat">Ещё раз</Label>
              <Input
                id="repeat"
                type="password"
                autoComplete="new-password"
                required
                value={repeat}
                onChange={(e) => setRepeat(e.target.value)}
                disabled={busy}
              />
            </div>
            {error && (
              <p className="rounded-md border border-destructive/30 bg-destructive/5 px-3 py-2 text-xs text-destructive-ink">
                {error}
              </p>
            )}
            <Button type="submit" className="w-full" disabled={busy}>
              {busy ? "Сохраняем…" : "Войти"}
            </Button>
          </form>
        )}
      </div>
    </div>
  );
}
